import type { LexProduct } from '@/shared/hooks/api/product'
import type { ProductSource } from '@/shared/hooks/api/productSource'
import {
  isProductSourceIngestionInFlight,
  isProductSourceUploadFailed,
  isProductSourceUploadIncomplete,
} from '@/features/product-catalog/utils/productSourceUploadStatus'

export type ProductChatUnavailableReason = 'processing' | 'failed' | 'unavailable'

export type ProductChatAvailability = {
  hasMetadata: boolean
  hasCompletedSource: boolean
  hasProcessingSource: boolean
  hasOnlyFailedSources: boolean
  canStartProductChat: boolean
  chatUnavailableReason: ProductChatUnavailableReason | null
}

export type ProductSourceStatusFields = Pick<
  ProductSource,
  'ingestionStatus' | 'fileUploadStatus' | 'filePathInBucket'
>

export function productHasMetadata(product: LexProduct): boolean {
  return (
    Boolean(product.productModel?.trim()) ||
    product.yearOfManufacture != null ||
    Boolean(product.description?.trim())
  )
}

export function isProductSourceInFlight(
  source: ProductSourceStatusFields
): boolean {
  return isProductSourceIngestionInFlight(source)
}

export function isProductSourceFailed(
  source: ProductSourceStatusFields
): boolean {
  if (isProductSourceUploadFailed(source.fileUploadStatus)) {
    return true
  }
  if (source.ingestionStatus === 'FAILED') {
    return true
  }
  if (isProductSourceUploadIncomplete(source)) {
    return false
  }
  return false
}

export function isProductSourceUploadStuckOrFailed(
  source: ProductSourceStatusFields
): boolean {
  if (isProductSourceUploadFailed(source.fileUploadStatus)) {
    return true
  }
  return isProductSourceUploadIncomplete(source)
}

export function getProductChatAvailability(input: {
  product: LexProduct
  sources: ReadonlyArray<ProductSourceStatusFields>
}): ProductChatAvailability {
  const hasMetadata = productHasMetadata(input.product)
  const hasCompletedSource = input.sources.some(
    (source) => source.ingestionStatus === 'COMPLETED'
  )
  const hasProcessingSource = input.sources.some(isProductSourceInFlight)
  const hasOnlyFailedSources =
    input.sources.length > 0 &&
    !hasCompletedSource &&
    !hasProcessingSource &&
    input.sources.every(isProductSourceFailed)

  const canStartProductChat = hasMetadata || hasCompletedSource

  let chatUnavailableReason: ProductChatUnavailableReason | null = null
  if (!canStartProductChat) {
    if (hasOnlyFailedSources) {
      chatUnavailableReason = 'failed'
    } else if (hasProcessingSource) {
      chatUnavailableReason = 'processing'
    } else {
      chatUnavailableReason = 'unavailable'
    }
  }

  return {
    hasMetadata,
    hasCompletedSource,
    hasProcessingSource,
    hasOnlyFailedSources,
    canStartProductChat,
    chatUnavailableReason,
  }
}
